import { useEffect, useState } from 'react';
import { VStack, Heading, Text, Button, Spinner, Circle } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acceptInvitation } from '../api/organizationApi';

/**
 * Accepts a team invitation from an emailed link (/invitations/accept?token=…).
 * Wrapped in RequireAuth (the invitee signs in first) but NOT OnboardingGuard —
 * invitees join an existing org rather than onboarding a new one.
 */
export function InvitationAcceptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(t('invitations.noToken'));
      return;
    }
    let cancelled = false;
    acceptInvitation(token)
      .then((res) => {
        if (cancelled) return;
        setOrgName(res.organization?.name ?? '');
        setStatus('success');
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus('error');
        setError(e instanceof Error ? e.message : t('invitations.failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <VStack minH="70vh" justify="center" align="center" p={6} spacing={5} maxW="480px" mx="auto">
      {status === 'loading' && (
        <>
          <Spinner size="xl" color="brand.500" thickness="3px" />
          <Text color="gray.500">{t('invitations.accepting')}</Text>
        </>
      )}

      {status === 'success' && (
        <>
          <Circle size="72px" bg="green.50">
            <Text fontSize="2xl">✓</Text>
          </Circle>
          <Heading size="lg" textAlign="center">
            {t('invitations.joined', { org: orgName })}
          </Heading>
          <Button colorScheme="brand" onClick={() => navigate('/app')}>
            {t('invitations.goToApp')}
          </Button>
        </>
      )}

      {status === 'error' && (
        <>
          <Circle size="72px" bg="red.50">
            <Text fontSize="2xl">✕</Text>
          </Circle>
          <Heading size="md" textAlign="center">
            {t('invitations.errorTitle')}
          </Heading>
          <Text color="red.500" textAlign="center" fontSize="sm">
            {error}
          </Text>
          <Button variant="outline" onClick={() => navigate('/app')}>
            {t('invitations.goToApp')}
          </Button>
        </>
      )}
    </VStack>
  );
}

export default InvitationAcceptPage;
